import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { NotificationResponse } from '../clients/rust-api-client';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import { GroupedList } from '../shell/GroupedList';
import { FilterBar } from '../violation/linear/FilterBar';
import { canSendMemberMessage, UserType } from '../constants/UserTypes';
import { useSyncedCommunications } from '../sync/hooks';
import { useUser } from '../hooks/UserContext';
import {
  useCommandPalette,
  useMessageSentListener,
} from '../shell/CommandPaletteProvider';
import { CreateButton } from '../shell/CreateButton';
import {
  fetchAllNotifications,
  hoaOptions,
  isCommunication,
  notificationTypeLabel,
  typeOptions,
} from '../notification/notificationMeta';
import {
  CommunicationFilters,
  EMAIL_OPTIONS,
  EMPTY_COMMUNICATION_FILTERS,
  READ_OPTIONS,
  applyCommunicationFilters,
  formatTimeOfDay,
  groupByDay,
} from './communicationMeta';
import { formatTimestamp } from '../utils/dateUtils';

/**
 * Everything sent to homeowners (#623) — the rows the member portal shows
 * them. Read-only: the read state is the homeowner's, so marking or
 * deleting from here would change their inbox (#626).
 */
const CommunicationListComponent: React.FC = () => {
  const navigate = useNavigate();
  const [fetched, setRows] = useState<NotificationResponse[]>([]);
  const [fetching, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<CommunicationFilters>(
    EMPTY_COMMUNICATION_FILTERS
  );
  const { openSendMessage } = useCommandPalette();
  const { userType } = useUser();
  const [showSent, setShowSent] = useState(false);
  // From the sync engine's copy when there is one (#561): no paging
  // through the whole mail history, and a message sent from anywhere
  // appears as it goes out. Not for a board member, whose copy holds no
  // residents' mail until #626 is decided; they fetch, as before.
  const synced = useSyncedCommunications(userType !== UserType.BOARD_MEMBER);
  const haveSynced = synced !== null;
  const rows = synced ?? fetched;
  const loading = !haveSynced && fetching;

  const fetchRows = useCallback(async (): Promise<void> => {
    if (haveSynced) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const all = await fetchAllNotifications();
      setRows(all.filter(isCommunication));
      setError(null);
    } catch (err) {
      console.error('Error fetching communications:', err);
      setError("Couldn't load communications. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [haveSynced]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const visible = useMemo(
    () => applyCommunicationFilters(rows, filters),
    [rows, filters]
  );
  const groups = useMemo(() => groupByDay(visible), [visible]);

  // Any send — this list's button, the sidebar's "New…" or ⌘K — lands
  // here, so any of them refreshes the list.
  useMessageSentListener(() => {
    setShowSent(true);
    fetchRows();
  });

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Flush against the panel's edges, alerts need their own inset. */}
      <div className="px-3 pt-3 empty:hidden">
        {error && (
          <ShowAlert
            alertType="error"
            alertTitle="Error"
            alertMessage={error}
            isVisible={true}
            onClose={() => setError(null)}
            timeout={5000}
            isInline={true}
          />
        )}
        {showSent && (
          <ShowAlert
            alertType="success"
            alertTitle="Sent"
            alertMessage="Message sent."
            isVisible={true}
            onClose={() => setShowSent(false)}
            timeout={5000}
            isInline={true}
          />
        )}
      </div>

      <FilterBar
        fields={[
          {
            field: 'HOA',
            options: hoaOptions(rows),
            values: filters.hoas,
            onChange: (hoas) => setFilters((f) => ({ ...f, hoas })),
          },
          {
            field: 'Type',
            options: typeOptions(rows),
            values: filters.types,
            onChange: (types) => setFilters((f) => ({ ...f, types })),
          },
          {
            field: 'Email',
            options: EMAIL_OPTIONS.map((o) => ({ ...o })),
            values: filters.email,
            onChange: (email) => setFilters((f) => ({ ...f, email })),
          },
          {
            field: 'Homeowner',
            options: READ_OPTIONS.map((o) => ({ ...o })),
            values: filters.read,
            onChange: (read) => setFilters((f) => ({ ...f, read })),
          },
        ]}
        search={filters.search}
        onSearch={(search) => setFilters((f) => ({ ...f, search }))}
        searchLabel="Search communications by recipient, subject or message"
        onClearAll={() =>
          setFilters((f) => ({
            ...EMPTY_COMMUNICATION_FILTERS,
            search: f.search,
          }))
        }
        shown={visible.length}
        total={rows.length}
        trailing={
          canSendMemberMessage(userType) && (
            <CreateButton label="Send message" onClick={() => openSendMessage()} />
          )
        }
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <GroupedList
          groups={groups}
          getKey={(c) => c.id}
          rowLabel={(c) => `${c.title}, to ${c.recipient_name}`}
          onOpen={(c) => navigate(`/communications/${c.id}`)}
          emptyMessage={
            rows.length === 0
              ? 'Nothing has been sent to homeowners yet.'
              : 'No communications match these filters.'
          }
          renderRow={(c) => (
            <>
              <span className="w-40 shrink-0 truncate font-medium text-fg">
                {c.recipient_name}
              </span>
              <span className="min-w-0 flex-1 truncate">
                <span className="text-fg">{c.title}</span>
                <span className="ml-2 text-fg-muted">{c.message}</span>
              </span>
              <span className="hidden rounded-full border border-line px-2 py-0.5 text-[12px] text-fg-muted md:inline">
                {notificationTypeLabel(c.notification_type)}
              </span>
              <span className="hidden w-36 truncate text-[12px] text-fg-muted lg:inline">
                {c.hoa_name}
              </span>
              <span
                className={clsx(
                  'hidden w-16 text-[12px] sm:inline',
                  c.email_sent ? 'text-fg-muted' : 'text-warning'
                )}
              >
                {c.email_sent ? 'Emailed' : 'No email'}
              </span>
              <span
                className={clsx(
                  'w-14 text-[12px]',
                  c.is_read ? 'text-fg-muted' : 'font-medium text-accent-fg'
                )}
              >
                {c.is_read ? 'Read' : 'Unread'}
              </span>
              <span
                className="hidden w-20 shrink-0 whitespace-nowrap text-right text-[12px] tabular-nums text-fg-muted sm:inline"
                title={formatTimestamp(c.created_at)}
              >
                {formatTimeOfDay(c.created_at)}
              </span>
            </>
          )}
        />
      </div>
    </div>
  );
};

export default CommunicationListComponent;
